import { useCallback, useRef, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useGetCertificateByIdQuery } from "../../store/api/postgresCertificatesApi";
import html2canvas from "html2canvas";
import { useToast } from "../../context/CustomToast";
import { useAuth } from "../../supabase/AuthProvider";

export const useCertificateDetailHook = () => {
  const { certificateId } = useParams<{ certificateId: string }>();
  const navigate = useNavigate();
  const certificateRef = useRef<HTMLDivElement>(null);
  const [showLinkedInModal, setShowLinkedInModal] = useState(false);
  const [pendingLinkedInUrl, setPendingLinkedInUrl] = useState("");
  const [pollWhilePending, setPollWhilePending] = useState(false);
  const { showToast } = useToast();
  const { session, user } = useAuth();
  const activeToken = useRef(session?.access_token);
  activeToken.current = session?.access_token;
  useEffect(() => {
    activeToken.current = session?.access_token;
    return () => {activeToken.current = undefined;};
  }, [session?.access_token]);
  const [preview, setPreview] = useState<{key: string; url: string} | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  // Fetch the specific certificate by ID
  const {
    data: certificate,
    isLoading,
    error,
  } = useGetCertificateByIdQuery(certificateId || "", {
    skip: !certificateId,
    pollingInterval: pollWhilePending ? 10000 : 0,
    skipPollingIfUnfocused: true,
    refetchOnMountOrArgChange: true,
  });

  const previewKey = `${user?.id}:${session?.access_token}:${certificate?.id}:${certificate?.thumbnail_uri}`;
  useEffect(() => {
    setPreview(null);
    setPreviewError(null);
    if (!certificate?.thumbnail_uri || !certificate.id || !session?.access_token || !user?.id) { return; }
    const abort = new AbortController();
    let url: string | undefined;
    const api = (process.env.REACT_APP_BACKEND_BASE_URL || "").replace(/\/$/, "");
    void (async () => {
      try {
        const response = await fetch(`${api}/certificates/owner-artifacts/${encodeURIComponent(certificate.id)}/png`, {
          headers: {Authorization: `Bearer ${session.access_token}`}, signal: abort.signal,
        });
        if (!response.ok || !response.headers.get("content-type")?.startsWith("image/png")) { throw new Error("Preview unavailable"); }
        const blob = await response.blob();
        if (abort.signal.aborted) { return; }
        url = URL.createObjectURL(blob);
        setPreview({key: previewKey, url});
      } catch {
        if (!abort.signal.aborted) { setPreviewError("Certificate preview could not be loaded."); }
      }
    })();
    return () => {abort.abort(); if (url) { URL.revokeObjectURL(url); }};
  }, [certificate?.id, certificate?.thumbnail_uri, previewKey, session?.access_token, user?.id]);

  useEffect(() => {
    setPollWhilePending(certificate?.download_uri === null);
  }, [certificate?.download_uri]);

  const generateCertificateImage = async () => {
    if (!certificateRef.current) {return null;}
    const canvas = await html2canvas(certificateRef.current, {
      scale: 2,
      useCORS: true,
      backgroundColor: "#ffffff",
    });
    return canvas.toDataURL("image/png");
  };

  const handleShareOnLinkedIn = async () => {
    // 1. Generate certificate image
    const imgData = await generateCertificateImage();
    if (!imgData) {return;}

    // 2. Trigger download for user
    const link = document.createElement("a");
    link.href = imgData;
    link.download = `${certificate?.course_name || "Certificate"}-Certificate.png`;
    link.click();

    // 3. Copy showcase text to clipboard
    const showcaseText = `I've just completed the "${certificate?.course_name || "Course"}" course on Pedagogue! 🎉\n\nCheck out my certificate below. #Pedagogue #Achievement #AI`;
    try {
      await navigator.clipboard.writeText(showcaseText);
    } catch (e) {
      // fallback: do nothing, user can copy manually if needed
    }

    // 4. Prepare LinkedIn share URL
    const shareUrl = encodeURIComponent(window.location.href);
    const linkedInUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${shareUrl}`;

    // Set URL first, then open modal
    setPendingLinkedInUrl(linkedInUrl);
    setTimeout(() => setShowLinkedInModal(true), 100);
  };

  const handleDownload = useCallback(async () => {
    if (!certificate) {return;}
    if (!certificate.download_uri) {return;}
    if (!session?.access_token) {return;}
    try {
      const api = (process.env.REACT_APP_BACKEND_BASE_URL || "").replace(/\/$/, "");
      const response = await fetch(`${api}/certificates/owner-artifacts/${encodeURIComponent(certificate.id)}/pdf`, {
        headers: {Authorization: `Bearer ${session.access_token}`},
      });
      if (!response.ok || !response.headers.get("content-type")?.startsWith("application/pdf")) { throw new Error("Download unavailable"); }
      const blob = await response.blob();
      if (activeToken.current !== session.access_token) { return; }
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url; link.download = "certificate.pdf";
      document.body.appendChild(link); link.click(); link.remove();
      const revoke = URL.revokeObjectURL.bind(URL);
      window.setTimeout(() => revoke(url), 1000);
    } catch {showToast("Certificate download could not be completed.", "error");}
  }, [certificate, session?.access_token, showToast]);

  const handleShare = useCallback(async () => {
    if (!certificate) {return;}

    if (navigator?.share) {
      try {
        await navigator.share({
          title: `${certificate.course_name} Certificate`,
          text: `I just completed ${certificate.course_name} and earned my certificate!`,
          url: window.location.href,
        });
      } catch (error) {
        console.error("Error sharing:", error);
      }
    } else {
      // Fallback for browsers that don't support native sharing
      try {
        await navigator.clipboard.writeText(window.location.href);
        showToast("Certificate link copied to clipboard!", "success");
      } catch (error) {
        console.error("Error copying link:", error);
        showToast("Failed to copy link to clipboard", "error");
      }
    }
  }, [certificate, showToast]);

  const handleBackToCertificates = useCallback(() => {
    navigate("/certificates");
  }, [navigate]);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Redirect to certificate not found if certificate doesn't exist
  useEffect(() => {
    if (!isLoading && !certificate && certificateId && !error) {
      navigate("/certificates/not-found", { replace: true });
    }
  }, [certificate, certificateId, isLoading, navigate, error]);

  // Get error message based on error type
  const getErrorMessage = useCallback(() => {
    if (!error) {return null;}

    // Handle different error types
    if ("status" in error) {
      switch (error.status) {
      case 404:
        return "Certificate not found";
      case 400:
        return "Invalid certificate ID";
      case 500:
        return "Server error. Please try again later.";
      default:
        return "Failed to fetch certificate";
      }
    }

    return "Failed to fetch certificate";
  }, [error]);

  // Show loading state while fetching certificate
  if (isLoading) {
    return {
      certificate: null,
      certificateRef,
      handleDownload,
      handleShare,
      handleShareOnLinkedIn,
      handleBackToCertificates,
      isLoading: true,
      error: null,
      showLinkedInModal,
      setShowLinkedInModal,
      pendingLinkedInUrl,
    };
  }

  // Show error state
  if (error || previewError) {
    return {
      certificate: null,
      certificateRef,
      handleDownload,
      handleShare,
      handleShareOnLinkedIn,
      handleBackToCertificates,
      isLoading: false,
      error: previewError || getErrorMessage(),
      showLinkedInModal,
      setShowLinkedInModal,
      pendingLinkedInUrl,
    };
  }

  // If no certificate found, return null (will trigger redirect)
  if (!certificate) {
    return {
      certificate: null,
      certificateRef,
      handleDownload,
      handleShare,
      handleBackToCertificates,
      isLoading: false,
      error: null,
      showLinkedInModal,
      setShowLinkedInModal,
      pendingLinkedInUrl,
    };
  }

  return {
    certificate: {...certificate, thumbnail_uri: preview?.key === previewKey ? preview.url : null},
    certificateRef,
    handleDownload,
    handleShare,
    handleShareOnLinkedIn,
    handleBackToCertificates,
    isLoading: false,
    error: null,
    showLinkedInModal,
    setShowLinkedInModal,
    pendingLinkedInUrl,
  };
};
